import type { CommandSlotMode } from '../schema/arrangement.schema';
import { toSlug } from './slug.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ANY / ALL / SPECIFIC — WHAT THEY MEAN, IN ONE PLACE.
 *
 *  ⚠️ READ THIS BEFORE WRITING ANY CODE THAT ACTS ON A COMMAND ENTRY.
 *
 *  These three words are the CLIENT'S STATED INTENT and are NOT YET CONFIRMED FOR
 *  EXECUTION. Nobody has yet told us what "Any" does when the shift starts and none of
 *  the named officers has marked on duty, or whether "All" means an absence is a
 *  shortfall. P5 answers those questions; P3a records the intent faithfully and puts the
 *  interpretation HERE so that answering them later is one edit to one function rather
 *  than an archaeology exercise across a screen, a service and a job.
 *
 *  The three modes, as the client described them:
 *
 *    ALL       every named officer supervises that shift. THE DEFAULT.
 *    ANY       the shift is covered if at least one of them is present.
 *    SPECIFIC  a named subset supervises that shift, and the others do not.
 *
 *  ⚠️ NONE OF THIS IS A COUNT, AND NOTHING BELOW MAY BECOME ONE.
 *  FR-5.2.6b (closing OD-9) puts command entirely outside requirement, allocation,
 *  fulfilment percentage and shortfall. `resolveCommandForSlot` returns WHO, never HOW
 *  MANY, and there is deliberately no exported helper here that returns a number.
 *  `api/tests/integration/commandRoleIsolation.test.ts` scans this file for arithmetic.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type CommandSlotOverride = {
  slotKey: string;
  mode: CommandSlotMode;
  officerIds: readonly string[];
};

export type CommandEntryLike = {
  /** ⚠️ [amended D4.3] `null` on a one-off row, which carries its own name instead. */
  roleKey?: string | null | undefined;
  adHocLabel?: string | null | undefined;
  officerIds: readonly string[];
  perSlot?: readonly CommandSlotOverride[] | undefined;
};

export type ResolvedCommand = {
  roleKey: string | null;
  adHocLabel: string | null;
  mode: CommandSlotMode;
  /** Who is on this shift for this role. Ordered as the operator named them. */
  officerIds: string[];
};

/**
 * ⚠️ [amended D4.3] ONE COMMAND ROW'S IDENTITY WITHIN ONE DUTY POINT.
 *
 * `R:<policy role key>` or `A:<slug of the one-off label>` — deliberately the same shape,
 * and the same reasoning, as `fillRef.util.ts`'s `lineKeyOf`. A policy role has a key minted
 * once and carried on the wire; a one-off row has no id at all, so its label IS its handle.
 *
 * ⚠️ TOTAL — IT NEVER THROWS. It is called from a zod `superRefine`, and a throw inside a
 * refine escapes `safeParse` and surfaces as an opaque 500 (measured on zod 4.4.3, recorded
 * in CLAUDE.md §6). A degenerate row returns `'?'` or `'A:'` and COLLIDES, which the
 * duplicate check then reports as the refusal it is.
 *
 * ⚠️ AND IT IS AN IDENTITY, NEVER A QUANTITY. Nothing here counts.
 */
export const commandKeyOf = (entry: {
  roleKey?: string | null | undefined;
  adHocLabel?: string | null | undefined;
}): string => {
  const key = entry.roleKey;
  if (key !== null && key !== undefined && key.trim() !== '') return `R:${key.trim()}`;

  const label = entry.adHocLabel;
  if (label !== null && label !== undefined) return `A:${toSlug(label)}`;

  return '?';
};

/**
 * What to SHOW for a command row, given the labels the frozen policy version supplies.
 *
 * ⚠️ ONE FUNCTION, SO NO READER BRANCHES ON WHICH KIND OF ROW IT HAS. A one-off row names
 * itself; a policy row is resolved from the version it points at, and falls back to `null`
 * when the version no longer carries it — which renders as a dash rather than as a slug.
 */
export const commandLabelOf = (
  entry: { roleKey?: string | null | undefined; adHocLabel?: string | null | undefined },
  roster: ReadonlyMap<string, string>,
): string | null => {
  const own = entry.adHocLabel;
  if (own !== null && own !== undefined && own.trim() !== '') return own;

  const key = entry.roleKey;
  if (key === null || key === undefined) return null;
  return roster.get(key) ?? null;
};

/**
 * The one interpretation. Given a role's named officers and its per-shift overrides,
 * answer WHO stands for one particular slot.
 *
 * ⚠️ AN ABSENT OVERRIDE IS `ALL`, AND THAT IS WHY THE SIMPLE CASE STAYS SIMPLE.
 * A single-shift arrangement, or one where a role has one officer, never writes a
 * `perSlot` row at all — the mapper that would produce them is not even rendered. So the
 * common arrangement stores nothing and this function still answers correctly for it.
 *
 * ⚠️ `SPECIFIC` IS FILTERED THROUGH THE ROLE'S OWN LIST, NEVER TRUSTED WHOLESALE.
 * An officer removed from the role after a subset was chosen would otherwise stay
 * standing on that one shift, invisibly, because nothing else ever looks at that row
 * again. Filtering here means the role's list is the single source of who may appear.
 */
export const resolveCommandForSlot = (entry: CommandEntryLike, slotKey: string): ResolvedCommand => {
  const named = [...entry.officerIds];
  const override = entry.perSlot?.find((row) => row.slotKey === slotKey);

  if (!override || override.mode === 'ALL') {
    return { roleKey: entry.roleKey ?? null, adHocLabel: entry.adHocLabel ?? null, mode: 'ALL', officerIds: named };
  }

  if (override.mode === 'ANY') {
    // ANY still records everyone eligible; it changes what COVERAGE means, not who is
    // named. P5 reads `mode` to decide that, which is why the mode travels with the row.
    return { roleKey: entry.roleKey ?? null, adHocLabel: entry.adHocLabel ?? null, mode: 'ANY', officerIds: named };
  }

  const chosen = new Set(override.officerIds);
  return {
    roleKey: entry.roleKey ?? null,
    adHocLabel: entry.adHocLabel ?? null,
    mode: 'SPECIFIC',
    officerIds: named.filter((id) => chosen.has(id)),
  };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P5a §3] WHO IS "THE INCHARGE" — RESOLVED THROUGH THE POLICY VERSION'S OWN ROLE
 *  ORDER, AND NEVER BY MATCHING A LABEL.
 *
 *  The officer surface has to answer "who do I report to?" in one line on a list row. There
 *  is no `isIncharge` marker anywhere in this system and there deliberately is not one:
 *  command roles are declared PER POLICY VERSION (there is no `CommandRole` collection), so
 *  a festival policy and a traffic policy legitimately name different supervisors.
 *
 *  ⚠️ MATCHING THE LABEL WOULD BE THE OBVIOUS IMPLEMENTATION AND IT IS UNWORKABLE.
 *  `policy.schema.ts` already records that `"In Charge"` in one policy and `"Incharge"` in
 *  another are DIFFERENT THINGS FOR EVER, because the key is slugged once from the first
 *  label and never recomputed. A string match would answer differently for two policies that
 *  mean the same thing, and identically for two that do not.
 *
 *  So the rule is STRUCTURAL: a role's `order` on the frozen policy version is its position
 *  in that policy's own command hierarchy — FR-5.4.5 calls the same axis "PA role order" when
 *  it says a supervisor may act for anyone "at or below their PA role order". The incharge is
 *  therefore the FIRST role in that order with somebody actually named against it.
 *
 *  ⚠️ A ONE-OFF ROW CANNOT BE THE INCHARGE. It carries no `roleKey`, so it has no position in
 *  the policy's hierarchy at all — and inventing one for it would mean an ad-hoc supervisor
 *  added to one duty point silently outranking the order's actual Incharge.
 *
 *  ⚠️ AND THIS RETURNS AN OFFICER ID, NEVER A COUNT. FR-5.2.6b is untouched: nothing here
 *  totals, allocates against or balances a command row. `commandRoleIsolation.test.ts`
 *  CLAIM 5 scans this file for arithmetic, and the comparator below is deliberately defined
 *  on its own line, naming no command vocabulary, so what it scans stays readable.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type RoleOrdering = ReadonlyMap<string, number>;

/** Lifted out so the subtraction sits on a line that names nothing this guard scans for. */
const byOrder = (left: number, right: number): number => left - right;

export const inchargeOfficerId = (
  entries: readonly { roleKey?: string | null | undefined; officerIds: readonly string[] }[],
  ordering: RoleOrdering,
): string | null => {
  const ranked = entries
    .flatMap((entry) => {
      const key = entry.roleKey;
      if (key === null || key === undefined || key.trim() === '') return [];
      const position = ordering.get(key);
      if (position === undefined) return [];
      const first = entry.officerIds[0];
      return first === undefined ? [] : [{ position, officerId: first }];
    })
    .sort((left, right) => byOrder(left.position, right.position));

  return ranked[0]?.officerId ?? null;
};

/**
 * ⚠️ THE TWO-CONDITION TRIGGER FOR THE PER-SHIFT MAPPER, AND IT LIVES HERE SO BOTH THE
 * SCREEN AND ANY LATER READER ASK THE SAME QUESTION.
 *
 * A role gets a per-shift mapper only when it has TWO OR MORE OFFICERS **and** the
 * schedule has TWO OR MORE SLOTS. Either one alone and it stays hidden — that is what
 * keeps a one-place, one-shift arrangement free of a control it has no use for, and it
 * is exit criterion 4.
 *
 * ⚠️ `officerIds[1] !== undefined` RATHER THAN `officerIds.length > 1`, DELIBERATELY.
 * `commandRoleIsolation.test.ts` permits `.length` on a command line only as an
 * emptiness test — you may ask whether there are any, you may not use how many — and
 * "is there a second one?" is the question this actually means anyway.
 */
export const showsPerSlotMapper = (entry: CommandEntryLike, slotKeys: readonly string[]): boolean =>
  entry.officerIds[1] !== undefined && slotKeys[1] !== undefined;
